import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { FaUsers, FaBriefcase, FaClipboardCheck, FaExclamationTriangle, FaServer, FaDatabase, FaMoneyBillWave, FaBell } from 'react-icons/fa';
import SimpleBarChart from '../components/SimpleBarChart';

const AdminStatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between group hover:border-red-200 transition-all">
        <div>
            <p className="text-slate-400 text-xs font-black uppercase tracking-widest mb-1">{title}</p>
            <h3 className="text-3xl font-black text-slate-800 group-hover:text-red-600 transition-colors">{value}</h3>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl shadow-lg ${color}`}>
            <Icon />
        </div>
    </div>
);

function AdminDashboard() {
    const [stats, setStats] = useState({ 
        users: { total: 0, pending_kyc: 0, buyers: 0, workers: 0 }, 
        jobs: { active: 0, completed: 0 }, 
        finances: { volume: 0, revenue: 0 },
        system_health: 'Operational' 
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const res = await apiClient.get('/admin/stats');
                setStats(res.data);
                setError('');
            } catch (err) {
                console.error(err);
                setError('Failed to fetch system statistics.');
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    // Derived Data for Visuals
    const userDistribution = [
        { label: 'Buyers', value: stats.users.buyers || 0 },
        { label: 'Workers', value: stats.users.workers || 0 },
        { label: 'Total', value: stats.users.total || 0 },
        { label: 'Pending', value: stats.users.pending_kyc || 0 },
    ];

    const jobActivity = [
        { label: 'Active', value: stats.jobs.active || 0 },
        { label: 'Done', value: stats.jobs.completed || 0 },
        { label: 'Total', value: (stats.jobs.active + stats.jobs.completed) || 0 },
    ];

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mb-4"></div>
            <p className="p-10 text-center text-slate-500 font-bold">Initializing Admin Console...</p>
        </div>
    );

    if (error) return (
        <div className="p-10 text-center bg-red-50 text-red-600 rounded-2xl font-bold border border-red-100">
            {error}
        </div>
    );

    return (
        <div className="space-y-8 font-sans">
            <div className="flex justify-between items-end border-b border-slate-200 pb-6">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">System Overview</h1>
                    <p className="text-slate-500 mt-1">Platform Status & Critical Metrics</p>
                </div>
                <span className="bg-green-100 text-green-700 px-4 py-1 rounded-full text-xs font-bold flex items-center">
                    <FaServer className="mr-2" /> Systems {stats.system_health}
                </span>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <AdminStatCard title="Total Users" value={stats.users.total} icon={FaUsers} color="bg-slate-800" />
                <AdminStatCard title="Active Jobs" value={stats.jobs.active} icon={FaBriefcase} color="bg-blue-600" />
                <AdminStatCard title="Pending KYC" value={stats.users.pending_kyc} icon={FaClipboardCheck} color="bg-orange-500" />
                <AdminStatCard title="Platform Revenue" value={`৳${stats.finances.revenue.toFixed(0)}`} icon={FaMoneyBillWave} color="bg-emerald-600" />
            </div>

            {/* Visuals & Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Charts */}
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <SimpleBarChart data={userDistribution} title="User Distribution" color="bg-slate-800" />
                    <SimpleBarChart data={jobActivity} title="Job Activity" color="bg-emerald-500" />
                </div>

                {/* Live Feed / Alerts */}
                <div className="space-y-6">
                    {/* Critical Alerts */}
                    <div className="bg-red-50 rounded-[2rem] p-6 border border-red-100">
                        <h3 className="text-sm font-bold text-red-800 mb-4 flex items-center uppercase tracking-wider">
                            <FaExclamationTriangle className="mr-2" /> Action Required
                        </h3>
                        <div className="space-y-3">
                            {stats.users.pending_kyc > 0 ? (
                                <div className="bg-white p-4 rounded-xl border border-red-100 text-sm shadow-sm flex justify-between items-center cursor-pointer hover:bg-red-50 transition-colors">
                                    <span className="font-bold text-slate-700">Pending KYC</span>
                                    <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-xs font-bold">{stats.users.pending_kyc}</span>
                                </div>
                            ) : (
                                <p className="text-red-400 text-xs italic">No urgent alerts.</p>
                            )}
                        </div>
                    </div>

                    {/* Recent Activity Feed */}
                    <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm">
                        <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center uppercase tracking-wider">
                            <FaBell className="mr-2 text-slate-400" /> Recent Activity
                        </h3>
                        <ul className="space-y-4">
                            <li className="flex gap-3 items-start">
                                <div className="w-2 h-2 mt-1.5 bg-emerald-500 rounded-full flex-shrink-0"></div>
                                <div>
                                    <p className="text-xs font-bold text-slate-700">New Job Posted</p>
                                    <p className="text-[10px] text-slate-400">2 minutes ago</p>
                                </div>
                            </li>
                            <li className="flex gap-3 items-start">
                                <div className="w-2 h-2 mt-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                                <div>
                                    <p className="text-xs font-bold text-slate-700">User Verified: ID #8821</p>
                                    <p className="text-[10px] text-slate-400">15 minutes ago</p>
                                </div>
                            </li>
                            <li className="flex gap-3 items-start">
                                <div className="w-2 h-2 mt-1.5 bg-purple-500 rounded-full flex-shrink-0"></div>
                                <div>
                                    <p className="text-xs font-bold text-slate-700">Payment Processed: ৳5000</p>
                                    <p className="text-[10px] text-slate-400">1 hour ago</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminDashboard;